import { alpha, Box, Button, Tooltip, Typography } from '@mui/material';
import React from 'react';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import IconMi from 'components/common/icon/IconMi';
import { AlertRuleScreenProps } from 'components/alert-rule-popup/index';
import { useTranslation } from 'react-i18next';
import AlertRuleWorkflowInfo from 'components/alert-rule-popup/parts/AlertRuleWorkflowInfo';

interface AlertRuleScreenWorkflowProps extends AlertRuleScreenProps {
    onChange?: (text: any) => void;
}

export default function AlertRuleScreenWorkflow(props: AlertRuleScreenWorkflowProps) {
    const { alertRuleData, wizardData, updateAlertRuleData = () => {} } = props;
    const { t } = useTranslation(['components/alert-rule-popup/alert_rule_popup']);

    const select_workflow_id: FormComponentValue[] = wizardData.form.workflow_id.data.map((item: any) => {
        return {
            label: item.label,
            value: item.value,
            hidden: false,
            disabled: !!item.disabled,
        };
    });

    const workflow_priorities: FormComponentValue[] = wizardData.form.workflow_priorities.data.map((item: any) => {
        return {
            label: item.name,
            value: item.workflow_priority_id,
        };
    });

    if (alertRuleData.alert_visibility === 'private')
        return (
            //dWorkflowPrivateHint
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                    padding: 2,
                    mt: 2,
                }}
            >
                <Box sx={{ fontSize: 16, lineHeight: 'normal' }}>{t('form.workflow.alert_is_private_warning')}</Box>
            </Box>
        );

    return (
        <>
            {/*dWorkflowWrapper*/}
            <Box>
                <Typography
                    variant="heading"
                    sx={{ borderBottomColor: (theme) => alpha(theme.palette.primary.main, 0.4), mb: 2 }}
                >
                    {t('form.workflow.section_title')}
                </Typography>
                <Box>
                    <Box sx={{}}>{t('form.workflow.workflow_id_label')}</Box>
                    <Box sx={{ display: 'inline-flex', mr: 0.5 }}>
                        <Box sx={{ width: '324px' }}>
                            <ReactSelect
                                data={select_workflow_id}
                                value={[alertRuleData.workflow_id]}
                                update={(newWorkflowId) => {
                                    if (wizardData.init_workflow_id == newWorkflowId) {
                                        updateAlertRuleData({
                                            workflow_instance_id: wizardData.init_workflow_instance_id,
                                        });
                                    } else {
                                        updateAlertRuleData({ workflow_instance_id: 0 });
                                    }
                                    updateAlertRuleData({ workflow_id: newWorkflowId });
                                }}
                            />
                        </Box>
                        {wizardData.priv_create_workflow && (
                            <Tooltip title={String(t('form.workflow.add_new_workflow_btn_tooltip'))}>
                                <Button
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                    onClick={() => {
                                        updateAlertRuleData({ workflow_id: 'new' });
                                    }}
                                    className={'min-width--icon'}
                                >
                                    <IconMi icon="new" />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {alertRuleData.workflow_id == '0' && ( //no_workflow_is_selected_msg
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                            px: 1.5,
                            py: 1,
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.02),
                            borderRadius: '4px',
                            my: 2,
                        }}
                        onClick={(event: any) => {
                            const el = event.target.closest('a');
                            if (el && event.currentTarget.contains(el)) {
                                updateAlertRuleData({ workflow_id: 'new' });
                            }
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t(
                                `form.workflow.no_workflow_is_selected${
                                    wizardData.priv_create_workflow ? '_with_create' : ''
                                }`,
                                {
                                    createTagStart: '<a href="javascript:void(0)">',
                                    createTagEnd: '</a>',
                                },
                            ),
                        }}
                    />
                )}

                {/* Alert Priority Level: For exist workflow or new */}
                {alertRuleData.workflow_id != '0' && ( //dWorkflowPriorityWrapper
                    <Box sx={{ mt: 2, mb: 1.5 }}>
                        <Box>{t('form.workflow.workflow_priority_id_label')}</Box>
                        <Box>
                            <Box sx={{ width: '324px' }}>
                                <ReactSelect
                                    data={workflow_priorities}
                                    value={[alertRuleData.workflow_priority_id]}
                                    update={(val) => {
                                        updateAlertRuleData({ workflow_priority_id: val });
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* #workflow_name: This Alert Rule already has users subscribed to the {name}... */}
            {Number(wizardData.init_workflow_id) > 0 &&
                wizardData.init_workflow_id != alertRuleData.workflow_id && ( //id="workflow_name"
                    <Box
                        sx={{ mb: 1.5 }}
                        dangerouslySetInnerHTML={{
                            __html: t('form.workflow.alert_rule_already_has_users_subscribed_to_alert_workflow', {
                                name:
                                    select_workflow_id.find((option) => {
                                        return option.value == wizardData.init_workflow_id;
                                    })?.label ?? '',
                                boldTagStart: '<strong>',
                                boldTagEnd: '</strong>',
                            }),
                        }}
                    />
                )}

            {/* #dEventsToHide: Moving this alert rule into a workflow will... */}
            {Number(wizardData.events_to_hide) > 0 &&
                alertRuleData.workflow_id != '0' && ( //dEventsToHide
                    <Box sx={{ mb: 1.5 }}>{t('form.workflow.events_to_hide')}</Box>
                )}

            {alertRuleData.workflow_id != '0' && (
                <Box className="scroll_ver_OLD mod_sb_scroll_this_OLD">
                    {Number(alertRuleData.workflow_id) > 0 && ( //#workflow_info
                        <Box sx={{ mt: 1.5, mb: 1.5 }}>
                            <AlertRuleWorkflowInfo
                                key={Number(alertRuleData.workflow_id) + Number(alertRuleData.workflow_instance_id)}
                                workflowId={alertRuleData.workflow_id}
                                workflowInstanceId={alertRuleData.workflow_instance_id}
                            />
                        </Box>
                    )}

                    {alertRuleData.workflow_id == 'new' && ( //#new_workflow_info
                        <Box
                            sx={{ mt: 1.5, mb: 1.5 }}
                            dangerouslySetInnerHTML={{
                                __html: t(`form.workflow.you_can_subscribe_users_to_this_alert`, {
                                    boldTagStart: '<strong>',
                                    boldTagEnd: '</strong>',
                                }),
                            }}
                        />
                    )}
                </Box>
            )}
        </>
    );
}
